import { ColumnDef } from "@tanstack/react-table";
import { parseISO } from "date-fns";
import { MoreHorizontal, Pencil, Trash } from "lucide-react";
import { useMemo } from "react";
import { Link } from "react-router";

import { MultiFileUploadButton } from "@/components/multi-file-upload-button";
import { ServerDataTable } from "@/components/server-data-table";
import { SkuImage } from "@/components/skus/sku-image";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useGetVideosQuery } from "@/data/queries";
import { Video } from "@/data/types";
import { useDialogState } from "@/hooks/use-dialog-state";
import { canEditAsset } from "@/lib/permissions";
import { convertTimeFromSeconds } from "@/lib/utils";
import useStore from "@/state";

const columns: ColumnDef<Video>[] = [
  {
    id: "id",
    accessorKey: "id",
    header: "ID",
  },
  {
    id: "file_name",
    accessorKey: "file_name",
    header: "File Name",
    size: 320,
    cell: ({ row }) => {
      return (
        <div className="flex items-center gap-2 w-184">
          <Link
            to={`${row.original.preview_url}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <SkuImage
              srcSetWidths={[200, 400]}
              src={row.original.thumbnail_url}
              alt={row.original.file_name}
              className="max-w-24 max-h-24 min-w-24 min-h-24"
            />
          </Link>
          <Link
            to={`${row.original.preview_url}`}
            target="_blank"
            rel="noopener noreferrer"
            className="min-w-0"
          >
            <div className="min-w-0">
              <p className="font-medium truncate">{row.original.file_name}</p>
              <p className="text-sm text-muted-foreground truncate">
                {row.original.hls_url}
              </p>
            </div>
          </Link>
        </div>
      );
    },
  },
  {
    id: "duration_seconds",
    accessorKey: "duration_seconds",
    header: "Duration",
    cell: ({ row }) => {
      if (!row.original.duration_seconds) {
        return <div className="text-muted-foreground">Processing</div>;
      }

      return <div>{convertTimeFromSeconds(row.original.duration_seconds)}</div>;
    },
  },
  {
    id: "created_at",
    accessorKey: "created_at",
    header: "Uploaded at",
    cell: ({ row }) => {
      return (
        <div>
          {parseISO(row.original.created_at).toLocaleDateString()}
          <br />
          {parseISO(row.original.created_at).toLocaleTimeString()}
        </div>
      );
    },
  },
  {
    id: "actions",
    header: "Actions",
    accessorKey: "actions",
    cell: ({ row }) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { openDialog } = useDialogState();

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm">
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() =>
                openDialog({
                  name: "update-video",
                  params: row.original,
                })
              }
            >
              <Pencil /> Edit File Name (Coming Soon)
            </DropdownMenuItem>
            <DropdownMenuItem
              variant="destructive"
              onClick={() =>
                openDialog({
                  name: "delete-video",
                  params: row.original,
                })
              }
            >
              <Trash /> Delete Video
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];

export const Videos = () => {
  const { session } = useStore();
  const { data, isError, error, isFetching, refetch } = useGetVideosQuery();

  const assetEditAllowed = useMemo(
    () => canEditAsset(session?.permissions),
    [session],
  );

  if (isError) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <div>
      <div className="flex justify-between border-b pb-2">
        <h2 className="scroll-m-20 text-3xl font-medium tracking-tight first:mt-0">
          Videos
        </h2>
        <MultiFileUploadButton fileType="video" onFinish={() => refetch()} />
      </div>
      <ServerDataTable
        isLoading={isFetching}
        columns={columns}
        data={data?.videos}
        paginationInfo={data?.pagy}
        defaultColumnVisibility={{
          actions: assetEditAllowed,
        }}
        sortableColumns={["id", "file_name", "created_at"]}
      />
    </div>
  );
};
